import { CreatePosition, Position } from '@/types/positions'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import useDeleteDetail, {
  UseDeleteDetailOptions,
} from '../core-hooks/useDeleteDetail'
import useDeleteList, {
  UseDeleteListOptions,
} from '../core-hooks/useDeleteList'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import useUpdateDetail, {
  UseUpdateDetailOptions,
} from '../core-hooks/useUpdateDetail'
import useUpdateList, {
  UseUpdateListOptions,
} from '../core-hooks/useUpdateList'
import buildService from '../create-service'
import queryKeys from '../query-keys'

const service = buildService<Position>({
  pathCategory: 'positions',
})

export function useGetPosition({
  id,
  ...options
}: UseGetDetailOptions<Position>) {
  const query = useGetDetail<Position>({
    id,
    queryKey: queryKeys.positions.detail({
      id,
      filters: options.filters,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    position: query.data,
    ...query,
  }
}

export function useCreatePosition(
  options: UseCreateDetailOptions<CreatePosition, Position> = {},
) {
  const mutation = useCreateDetail<CreatePosition, Position>({
    serviceFn: service.createDetail,
    ...options,
  })

  return {
    createPosition: mutation.mutate,
    createPositionAsync: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * Sending `lookup_code`, `starting_point_info_type_id` or `params` makes a new
 * version of the position. Sending only `name` or `description` changes it in
 * place. Saving the same rule again does not pile up versions.
 */
export function useUpdatePosition(
  options: UseUpdateDetailOptions<Position> = {},
) {
  const mutation = useUpdateDetail<Position>({
    serviceFn: service.updateDetail,
    ...options,
  })

  return {
    updatePosition: mutation.mutate,
    updatePositionAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeletePosition(options: UseDeleteDetailOptions = {}) {
  const mutation = useDeleteDetail<Position>({
    serviceFn: service.deleteDetail,
    ...options,
  })

  return {
    deletePosition: mutation.mutate,
    deletePositionAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useGetPositions({
  filters,
  ...options
}: UseGetListOptions<Position> = {}) {
  const query = useGetList<Position>({
    filters,
    queryKey: queryKeys.positions.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    positions: query.allData,
    ...query,
  }
}

export function useUpdatePositions(
  options: UseUpdateListOptions<Position> = {},
) {
  const mutation = useUpdateList<Position>({
    serviceFn: service.updateList,
    ...options,
  })

  return {
    updatePositions: mutation.mutate,
    updatePositionsAsync: mutation.mutateAsync,
    ...mutation,
  }
}

export function useDeletePositions({
  sideEffectQueryKeys,
  listQueryKey,
  ...options
}: UseDeleteListOptions = {}) {
  const mutation = useDeleteList<Position>({
    serviceFn: service.deleteList,
    sideEffectQueryKeys,
    listQueryKey,
    ...options,
  })

  return {
    deletePositions: mutation.mutate,
    deletePositionsAsync: mutation.mutateAsync,
    ...mutation,
  }
}
